import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  ErrorState,
  ExportCsvButton,
  InfoHint,
  Modal,
  Skeleton,
} from '../../components/ui';
import { cn } from '../../lib/cn';
import { csvFilename, csvHours, csvMoney, downloadCsv } from '../../lib/csv';
import { formatHours, formatMoney } from '../../lib/format';
import { usePersonMonth, type PersonMonthDetail } from './api';
import { InProgressPill } from './components';

/**
 * ONE MONTH OF ONE PERSON — opened from a row of the monthly table.
 *
 * Jesse, 2026-10-06, on a contractor's page: *"is there a way to show hours
 * broken down by the selected month / contractor? by clicking on the
 * month..."*
 *
 * - The footing IS the row that was clicked: the server computes both with one
 *   function, so the breakdown cannot add up to a different month.
 * - "By agreement" isolates: press one and only its entries are listed; Show
 *   all puts the rest back (the Review dialog's gesture, so it reads the same).
 * - Revenue is hours at bill rates, exactly as the table means it; cost is
 *   hours × cost rate for an hourly person, and is not split for a salaried
 *   one — a salary is not paid by the project.
 */

type Entry = PersonMonthDetail['entries'][number];
type Agreement = PersonMonthDetail['agreements'][number];

/** `2026-08` → `August 2026`, read in UTC so the month never slips. */
function monthTitle(key: string): string {
  return new Date(`${key}-01T12:00:00Z`).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** `2026-08-03` → `Mon, Aug 3` — read in UTC, so the day never slips. */
function dayHeading(day: string): string {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

const hoursText = (n: number) => `${formatHours(n, { fixed: true })} h`;
const projectName = (x: { clientName: string; agreementName: string }) =>
  `${x.clientName} — ${x.agreementName}`;

export function PersonMonthDialog({
  personId,
  personName,
  month,
  onClose,
}: {
  personId: string;
  personName: string;
  /** YYYY-MM, or null when closed. */
  month: string | null;
  onClose: () => void;
}) {
  const query = usePersonMonth(personId, month);
  const [focus, setFocus] = useState<string | null>(null);
  useEffect(() => setFocus(null), [month]);

  if (!month) return null;
  const data = query.data;

  return (
    <Modal
      open
      onOpenChange={(next) => !next && onClose()}
      title={`${personName} · ${monthTitle(month)}`}
      description={
        data?.month.inProgress ? 'Month to date — hours by agreement and by entry' : 'Hours by agreement and by entry'
      }
      className="max-w-5xl"
      footer={
        data && data.entries.length > 0 ? (
          <div className="flex w-full justify-end">
            <ExportCsvButton
              count={data.entries.length}
              noun="entries"
              scope="in this month"
              onClick={() => exportMonth(data, personName)}
            />
          </div>
        ) : undefined
      }
    >
      {query.isPending ? (
        <div className="space-y-2">
          <Skeleton className="h-6 w-2/3" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : query.isError || !data ? (
        <ErrorState
          title="Couldn't load this month"
          message={query.error instanceof Error ? query.error.message : undefined}
          onRetry={() => void query.refetch()}
        />
      ) : (
        <MonthBody data={data} focus={focus} setFocus={setFocus} />
      )}
    </Modal>
  );
}

function MonthBody({
  data,
  focus,
  setFocus,
}: {
  data: PersonMonthDetail;
  focus: string | null;
  setFocus: (id: string | null) => void;
}) {
  const { month, agreements, entries, costBasis, uncostedHours } = data;
  const salaried = costBasis === 'salary';
  const focused = focus ? agreements.find((a) => a.agreementId === focus) ?? null : null;

  const visible = useMemo(
    () => (focus ? entries.filter((e) => e.agreementId === focus) : entries),
    [entries, focus],
  );
  const days = useMemo(() => {
    const map = new Map<string, Entry[]>();
    for (const e of visible) map.set(e.date, [...(map.get(e.date) ?? []), e]);
    return [...map.entries()];
  }, [visible]);

  // The footing: the clicked row when nothing is isolated, the agreement's
  // own rollup when one is — both server figures, never a browser re-sum.
  const foot = focused
    ? { hours: focused.hours, revenue: focused.revenue, cost: focused.cost, count: focused.entries }
    : { hours: month.hours, revenue: month.revenue, cost: salaried ? null : month.cost, count: entries.length };

  if (entries.length === 0) {
    return (
      <div className="space-y-3">
        <Summary data={data} />
        <p className="text-body-sm text-grey-500">No time was logged this month.</p>
      </div>
    );
  }

  return (
    <div className="max-h-[68vh] space-y-5 overflow-y-auto pr-1">
      <Summary data={data} />

      {/* By agreement — and a way to isolate one. */}
      <section>
        <h3 className="label !mb-1.5">By agreement</h3>
        <div className="rounded-lg border border-grey-200">
          <div
            className={cn(
              'hidden gap-x-4 border-b border-grey-200 bg-grey-50 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide text-grey-500 sm:grid',
              salaried
                ? 'grid-cols-[minmax(0,1fr)_5rem_6.5rem]'
                : 'grid-cols-[minmax(0,1fr)_5rem_6.5rem_6.5rem]',
            )}
          >
            <span>Agreement</span>
            <span className="text-right">Hours</span>
            <span className="text-right">Revenue</span>
            {!salaried && <span className="text-right">Cost</span>}
          </div>
          <ul className="divide-y divide-grey-100">
            {agreements.map((a) => (
              <AgreementLine
                key={a.agreementId}
                agreement={a}
                salaried={salaried}
                on={focus === a.agreementId}
                onToggle={() => setFocus(focus === a.agreementId ? null : a.agreementId)}
              />
            ))}
          </ul>
        </div>
      </section>

      {/* The entries, day by day. */}
      <section>
        <div className="mb-1.5 flex flex-wrap items-center gap-x-2">
          <h3 className="label !mb-0">Entries</h3>
          {focused && (
            <span className="text-caption text-grey-500">
              showing <span className="num">{visible.length}</span> of <span className="num">{entries.length}</span> ·{' '}
              {projectName(focused)}
              <button
                type="button"
                onClick={() => setFocus(null)}
                className="ml-2 font-semibold text-accent-700 hover:underline"
              >
                Show all
              </button>
            </span>
          )}
        </div>
        <div className="rounded-lg border border-grey-200">
          <div
            className={cn(
              'hidden gap-x-3 border-b border-grey-200 bg-grey-50 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide text-grey-500 md:grid',
              salaried
                ? 'grid-cols-[minmax(0,1.1fr)_minmax(0,1.5fr)_4.5rem_6.5rem]'
                : 'grid-cols-[minmax(0,1.1fr)_minmax(0,1.5fr)_4.5rem_6.5rem_6.5rem]',
            )}
          >
            <span>Agreement</span>
            <span>Note</span>
            <span className="text-right">Hours</span>
            <span className="text-right">Revenue</span>
            {!salaried && <span className="text-right">Cost</span>}
          </div>
          {days.map(([day, list]) => (
            <div key={day} className="border-b border-grey-100 last:border-b-0">
              <div className="flex items-baseline justify-between gap-3 bg-grey-50/60 px-3 py-1 text-caption">
                <span className="font-semibold text-grey-700">{dayHeading(day)}</span>
                <span className="num text-grey-600">
                  {hoursText(list.reduce((s, e) => s + e.hours, 0))}
                </span>
              </div>
              <ul>
                {list.map((e) => (
                  <EntryLine key={e.id} entry={e} salaried={salaried} />
                ))}
              </ul>
            </div>
          ))}
          <div
            className={cn(
              'grid items-baseline gap-x-3 border-t border-grey-200 bg-grey-50 px-3 py-2 text-body-sm font-semibold text-secondary',
              salaried ? 'grid-cols-[minmax(0,1fr)_auto_auto]' : 'grid-cols-[minmax(0,1fr)_auto_auto_auto]',
            )}
          >
            <span>
              {focused ? 'Shown' : 'Total'} · <span className="num">{foot.count}</span>{' '}
              {foot.count === 1 ? 'entry' : 'entries'}
            </span>
            <span className="num text-right">{hoursText(foot.hours)}</span>
            <span className="num text-right md:w-[6.5rem]">{formatMoney(foot.revenue)}</span>
            {!salaried && (
              <span className="num text-right md:w-[6.5rem]">
                {foot.cost != null ? formatMoney(foot.cost) : '—'}
              </span>
            )}
          </div>
        </div>
        {!salaried && uncostedHours > 0 && (
          <p className="mt-1.5 text-caption text-amber-700">
            <span className="num">{formatHours(uncostedHours, { fixed: true })}</span> h have no cost rate and are not
            in the cost.
          </p>
        )}
      </section>
    </div>
  );
}

/** The month in one line — the same figures as the row that opened it. */
function Summary({ data }: { data: PersonMonthDetail }) {
  const { month, costBasis } = data;
  const salaried = costBasis === 'salary';
  return (
    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
      <span className="num text-heading-3 font-bold text-secondary">{hoursText(month.hours)}</span>
      {month.inProgress && <InProgressPill />}
      <span className="text-body-sm text-grey-600">
        <span className="num">{formatHours(month.billableHours, { fixed: true })}</span> billable
      </span>
      <span className="text-body text-secondary">
        <span className="num font-bold">{formatMoney(month.revenue)}</span> revenue
      </span>
      <span className="text-body text-secondary">
        <span className="num font-bold">{formatMoney(month.cost)}</span> cost
        {salaried && <span className="text-body-sm text-grey-500"> · salary</span>}
      </span>
      <span className="inline-flex items-baseline gap-1 text-body text-secondary">
        <span className={cn('num font-bold', month.margin < 0 && 'num-negative')}>
          {formatMoney(month.margin, { parens: true })}
        </span>{' '}
        margin
        <InfoHint label="How this month is figured" size="sm">
          <p>
            <strong>Revenue</strong> is billable hours at each entry&rsquo;s bill rate — the value of the work, not
            cash collected.
          </p>
          <p className="mt-2">
            {salaried ? (
              <>
                <strong>Cost</strong> is the monthly salary
                {month.inProgress ? ', prorated to the business days elapsed so far' : ''}. It is not split by
                agreement or entry.
              </>
            ) : (
              <>
                <strong>Cost</strong> is each entry&rsquo;s hours × the cost rate on that agreement, or the
                person&rsquo;s own hourly cost when the agreement sets none.
              </>
            )}
          </p>
          <p className="mt-2">These are the same figures as the month&rsquo;s row in Monthly performance.</p>
        </InfoHint>
      </span>
    </div>
  );
}

function AgreementLine({
  agreement: a,
  salaried,
  on,
  onToggle,
}: {
  agreement: Agreement;
  salaried: boolean;
  on: boolean;
  onToggle: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        aria-pressed={on}
        className={cn(
          'grid w-full items-baseline gap-x-4 px-3 py-1.5 text-left text-body-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40',
          salaried
            ? 'grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[minmax(0,1fr)_5rem_6.5rem]'
            : 'grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[minmax(0,1fr)_5rem_6.5rem_6.5rem]',
          on ? 'bg-accent/5' : 'hover:bg-grey-50',
        )}
      >
        <span className="min-w-0 break-words">
          <span className="font-semibold text-secondary">{a.clientName}</span>
          <span className="text-grey-600"> — {a.agreementName}</span>
          <span className="ml-1.5 text-caption text-grey-500">
            <span className="num">{a.entries}</span> {a.entries === 1 ? 'entry' : 'entries'}
          </span>
          {/* A phone drops the money columns; they ride under the name. */}
          <span className="num block text-caption text-grey-500 sm:hidden">
            {formatMoney(a.revenue)} revenue{a.cost != null ? ` · ${formatMoney(a.cost)} cost` : ''}
          </span>
        </span>
        <span className="num text-right font-semibold text-secondary">{hoursText(a.hours)}</span>
        <span className="num hidden text-right text-grey-700 sm:block">{formatMoney(a.revenue)}</span>
        {!salaried && (
          <span className="num hidden text-right text-grey-700 sm:block">
            {a.cost != null ? formatMoney(a.cost) : '—'}
          </span>
        )}
      </button>
    </li>
  );
}

function EntryLine({ entry: e, salaried }: { entry: Entry; salaried: boolean }) {
  const chips = (
    <>
      {!e.billable && <Chip>non-billable</Chip>}
      {e.invoiced && <Chip>invoiced</Chip>}
      {e.running && <Chip tone="accent">running</Chip>}
      {e.deletedInSource && <Chip tone="amber">deleted in Harvest</Chip>}
    </>
  );
  const revenue = e.billable ? (
    <span title={e.billRate != null ? `${formatMoney(e.billRate)}/h` : undefined}>{formatMoney(e.revenue)}</span>
  ) : (
    <span className="text-grey-400">—</span>
  );
  const cost =
    e.cost == null ? (
      <span className="text-grey-400">—</span>
    ) : (
      <span title={e.costRate != null ? `${formatMoney(e.costRate)}/h` : 'no cost rate'}>{formatMoney(e.cost)}</span>
    );

  return (
    <li className="border-t border-grey-100 px-3 py-2 first:border-t-0">
      {/* Desktop: one row of columns. */}
      <div
        className={cn(
          'hidden items-baseline gap-x-3 text-body-sm md:grid',
          salaried
            ? 'grid-cols-[minmax(0,1.1fr)_minmax(0,1.5fr)_4.5rem_6.5rem]'
            : 'grid-cols-[minmax(0,1.1fr)_minmax(0,1.5fr)_4.5rem_6.5rem_6.5rem]',
        )}
      >
        <span className="min-w-0 break-words">
          <Link
            to={`/agreements/${e.agreementId}`}
            className="font-semibold text-secondary hover:underline"
          >
            {e.clientName}
          </Link>
          <span className="text-grey-600"> — {e.agreementName}</span>
          {e.taskName && <span className="block text-caption text-grey-500">{e.taskName}</span>}
        </span>
        <span className="min-w-0 break-words text-grey-700">
          {e.notes || <span className="text-grey-400">no note</span>}
          <span className="ml-1.5 inline-flex flex-wrap gap-1 align-middle">{chips}</span>
        </span>
        <span className={cn('num text-right font-semibold text-secondary', e.deletedInSource && 'line-through')}>
          {formatHours(e.hours, { fixed: true })}
        </span>
        <span className="num text-right text-grey-700">{revenue}</span>
        {!salaried && <span className="num text-right text-grey-700">{cost}</span>}
      </div>

      {/* Phone: a small card. */}
      <div className="text-body-sm md:hidden">
        <div className="flex items-baseline justify-between gap-3">
          <span className="min-w-0 break-words">
            <span className="font-semibold text-secondary">{e.clientName}</span>
            <span className="text-grey-600"> — {e.agreementName}</span>
          </span>
          <span className={cn('num shrink-0 font-semibold text-secondary', e.deletedInSource && 'line-through')}>
            {hoursText(e.hours)}
          </span>
        </div>
        {e.taskName && <div className="text-caption text-grey-500">{e.taskName}</div>}
        <div className="mt-0.5 break-words text-grey-700">
          {e.notes || <span className="text-grey-400">no note</span>}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-caption text-grey-600">
          {e.billable && (
            <span className="num">
              {formatMoney(e.revenue)} revenue
            </span>
          )}
          {!salaried && e.cost != null && <span className="num">{formatMoney(e.cost)} cost</span>}
          {chips}
        </div>
      </div>
    </li>
  );
}

function Chip({ children, tone = 'grey' }: { children: ReactNode; tone?: 'grey' | 'amber' | 'accent' }) {
  return (
    <span
      className={cn(
        'inline-block rounded px-1.5 py-px text-[11px] font-semibold leading-4',
        tone === 'grey' && 'bg-grey-100 text-grey-600',
        tone === 'amber' && 'bg-amber-50 text-amber-800',
        tone === 'accent' && 'bg-accent/10 text-accent-700',
      )}
    >
      {children}
    </span>
  );
}

/** One row per ENTRY (a row is a record), footed with the month's own figures. */
function exportMonth(data: PersonMonthDetail, personName: string) {
  const salaried = data.costBasis === 'salary';
  const header = [
    'Date',
    'Client',
    'Agreement',
    'Task',
    'Note',
    'Hours',
    'Billable',
    'Bill rate',
    'Revenue',
    ...(salaried ? [] : ['Cost rate', 'Cost']),
    'Status',
  ];
  const rows = data.entries.map((e) => [
    e.date,
    e.clientName,
    e.agreementName,
    e.taskName ?? '',
    e.notes ?? '',
    csvHours(e.hours),
    e.billable ? 'yes' : 'no',
    e.billRate != null ? csvMoney(e.billRate) : '',
    csvMoney(e.revenue),
    ...(salaried ? [] : [e.costRate != null ? csvMoney(e.costRate) : '', e.cost != null ? csvMoney(e.cost) : '']),
    [e.invoiced && 'invoiced', e.running && 'running', e.deletedInSource && 'deleted in Harvest']
      .filter(Boolean)
      .join('; '),
  ]);
  const total = [
    'Total',
    '',
    '',
    '',
    `${data.entries.length} entries`,
    csvHours(data.month.hours),
    '',
    '',
    csvMoney(data.month.revenue),
    ...(salaried ? [] : ['', csvMoney(data.month.cost)]),
    '',
  ];
  downloadCsv(
    csvFilename({ entity: 'time', view: personName, period: data.month.month }),
    [header, ...rows, total],
  );
}
